import React, { useEffect, useState } from "react";
import {
    Grid,
    TextField,
    LinearProgress,
    Pagination,
    Box,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import { useNewsController } from "./context/news-controller";
import { useNewsState } from "./context/news-context";
import { IListNewsRequest } from "../../model/base/request/list-news-request";
import PageHeader from "../components/shared/page-header";
import FilterPanel, { ResultsPanel } from '../components/shared/filter-panel';
import EmptyState, { hasActiveFilters } from '../components/shared/empty-state';
import ListSkeleton from '../components/shared/list-skeleton';
import NewsCard from '../components/home/news-card';

export function NewsComponent() {
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadMoreData, setLoadMoreData] = useState(false);
    const { loadData, loadCategories } = useNewsController();
    const { news, categories } = useNewsState();
    const [paramsRequet, setParamsRequest] = useState<IListNewsRequest>({ page: 0, pageSize: 12 } as IListNewsRequest);

    useEffect(() => {
        const initData = async () => {
            await loadCategories();
        };
        initData();
    }, []);

    useEffect(() => {
        const searchData = async () => {
            setLoadMoreData(true);
            await loadData(paramsRequet);
            setLoadMoreData(false);
            setInitialLoading(false);
        };
        searchData();
    }, [paramsRequet]);

    const handleClearFilters = () => {
        setParamsRequest({ page: 0, pageSize: 12 } as IListNewsRequest);
    };

    return (
        <Box>
            <PageHeader
                title="Notícias"
                subtitle="Acompanhe as últimas notícias e informações do município"
                breadcrumbs={[{ label: 'Notícias' }]}
            />

            {initialLoading ? (
                <ListSkeleton />
            ) : (
                <React.Fragment>
                    <FilterPanel>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={5}>
                                <TextField
                                    fullWidth
                                    label="Buscar notícias"
                                    placeholder="Digite para buscar notícias"
                                    value={paramsRequet.term || ''}
                                    onChange={(e) => {
                                        setParamsRequest({ ...paramsRequet, page: 0, term: e.target.value });
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Categoria</InputLabel>
                                    <Select
                                        value={paramsRequet.categoryId || ''}
                                        label="Categoria"
                                        onChange={(e) =>
                                            setParamsRequest({
                                                ...paramsRequet,
                                                page: 0,
                                                categoryId: e.target.value as number,
                                            })
                                        }
                                    >
                                        <MenuItem value="">
                                            <em>Todas</em>
                                        </MenuItem>
                                        {categories.map((element) => (
                                            <MenuItem value={element.id} key={element.id}>
                                                {element.description}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<ClearIcon />}
                                    onClick={handleClearFilters}
                                    fullWidth
                                    sx={{ height: '56px' }}
                                >
                                    Limpar Filtros
                                </Button>
                            </Grid>
                        </Grid>
                    </FilterPanel>

                    <ResultsPanel>
                        {loadMoreData && <LinearProgress sx={{ mb: 2 }} />}

                        {news?.items && news.items.length > 0 ? (
                            <Grid container spacing={3}>
                                {news.items.map((item) => (
                                    <Grid item xs={12} sm={6} md={4} key={item.id}>
                                        <NewsCard news={item} />
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <EmptyState title="Nenhuma notícia encontrada" filtered={hasActiveFilters(paramsRequet)} onClearFilters={handleClearFilters} />
                        )}

                        {news?.pageCount > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                <Pagination
                                    count={news.pageCount}
                                    page={(paramsRequet.page || 0) + 1}
                                    onChange={(_, page) => {
                                        setParamsRequest({ ...paramsRequet, page: page - 1 });
                                    }}
                                    disabled={loadMoreData}
                                    color="primary"
                                    showFirstButton
                                    showLastButton
                                    siblingCount={1}
                                    boundaryCount={1}
                                />
                            </Box>
                        )}
                    </ResultsPanel>
                </React.Fragment>
            )}
        </Box>
    );
}
